/**
 * <schmancy-install-app> — the affordance that gets this app onto the
 * visitor's home screen, dock or taskbar, whatever platform they are on.
 *
 * `install-route.ts` decides WHICH install is available; this component is
 * what the visitor touches. On the one platform family with an API it raises
 * the browser's own dialog; everywhere else it opens a sheet that teaches the
 * three-step gesture for THAT platform, with that platform's own wording and
 * glyph. On a platform with no route it renders nothing at all.
 *
 * PLACED, NOT RAISED. The default is an inline control the consumer puts
 * somewhere a visitor arrives on purpose — an account menu, a settings row, a
 * footer. That is the whole anti-nagware design: on every platform without an
 * API the "can install" signal is a standing fact rather than an event, true
 * on every load for the rest of the install's life, so anything that floats
 * itself off that fact floats itself again next week and the week after.
 *
 * `anchored` opts into the quiet bottom slot, and is honoured for the
 * `browser-prompt` route ALONE — the one route that is an event, decided by
 * the browser at this moment and spent the first time it is used. An anchored
 * host on iOS, macOS Safari or Firefox Android therefore paints nothing; place
 * an inline one in a menu for those. The rule is the component's rather than
 * the consumer's because "the signal never goes false" is exactly the trap a
 * consumer cannot see from the call site.
 *
 * Nothing here calls `focus()`, `blur()` or `scrollIntoView()`, and nothing
 * here is `<schmancy-alert>` or `$notify`: `alert.ts`'s `raise()` scrolls the
 * page and, over a reader with nothing focused, takes focus — an interruption
 * an offer nobody asked for has not earned — and a toast is one
 * auto-dismissing slot the next success
 * message silently replaces. The announcement is instead a stable polite live
 * region that outlives every message it carries — one that is inserted
 * alongside its own first text is routinely not announced at all, where one
 * always present announces each change. The visible pill carries no live role
 * of its own, or the sentence is announced twice.
 *
 * @slot - The control's own label. Defaults to the library's wording for the
 *         current route; a consumer naming several install controls on one
 *         surface names each of them here.
 */
import { reveal } from '@schmancy/directives'
import { componentText, SchmancyElement, schmancyElement, type ComponentTextKey } from '@schmancy/mixins/index'
import { css, html, type PropertyValues, type TemplateResult } from 'lit'
import { property } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import { exhaustMap, Subject, takeUntil } from 'rxjs'
import type { ButtonVariant } from '../button/button'
import '../button'
import '../icons'
import '../surface'
import '../typography'
import '../visually-hidden'
import { openInstallSteps } from './install-steps'
import { dismissInstall, installRoute, requestInstall, type OfferedInstallRoute } from './install-route'

/** What the live region and the anchored pill both say, read from one place so
 *  the announced sentence and the rendered one can never be two sentences. */
const ANNOUNCEMENT = {
	'browser-prompt': 'installApp.announce.browserPrompt',
	'ios-share': 'installApp.announce.iosShare',
	'macos-dock': 'installApp.announce.macosDock',
	'menu-shortcut': 'installApp.announce.menuShortcut',
} satisfies Record<OfferedInstallRoute, ComponentTextKey>

/** The glyph the visitor is about to go looking for on their own screen. */
const GLYPH = {
	'browser-prompt': 'install_desktop',
	'ios-share': 'ios_share',
	'macos-dock': 'dock_to_bottom',
	'menu-shortcut': 'add_home',
} satisfies Record<OfferedInstallRoute, string>

@schmancyElement('schmancy-install-app')
export class SchmancyInstallApp extends SchmancyElement {
	// The host is never a box: inline it hands its button straight to the
	// consumer's own flex or grid, and anchored it positions against the
	// viewport rather than joining a shell's column.
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	]

	/**
	 * Float the offer in a quiet slot at the bottom edge instead of rendering
	 * in place. Honoured for `browser-prompt` only — see the header.
	 */
	@property({ type: Boolean, reflect: true }) anchored = false

	/** The inner button's variant. `text` is the usual choice for a menu row. */
	@property({ reflect: true }) variant: ButtonVariant = 'filled'

	#stepsRequested$ = new Subject<void>()

	override connectedCallback(): void {
		super.connectedCallback()

		this.#stepsRequested$
			.pipe(
				// The Subject delivers synchronously, so `show()` still runs inside
				// the click's own call stack and anchors itself to it. exhaustMap: a
				// second tap while the sheet is open is the same tap.
				exhaustMap(() => openInstallSteps()),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/** Reflected so a consumer can hide the row, divider or heading AROUND this
	 *  control from CSS alone, without subscribing to anything. */
	override updated(changed: PropertyValues): void {
		super.updated(changed)
		this.setAttribute('route', installRoute.value)
	}

	override render(): TemplateResult {
		const route = installRoute.value
		const offer = route === 'installed' || route === 'unavailable' ? null : route
		// An anchored host answers for the event-backed route and stays silent for
		// every standing one, which is the whole nagware guard.
		const showing = offer !== null && (!this.anchored || offer === 'browser-prompt')

		const action = when(
			offer !== null,
			() => html`
				<schmancy-button
					variant=${this.variant}
					size="sm"
					type="button"
					@click=${() => (offer === 'browser-prompt' ? requestInstall() : this.#stepsRequested$.next())}
				>
					<schmancy-icon slot="prefix" aria-hidden="true">${GLYPH[offer]}</schmancy-icon>
					<slot>${componentText(offer === 'browser-prompt' ? 'installApp.action' : 'installApp.actionSteps')}</slot>
				</schmancy-button>
			`,
		)

		return html`
			<schmancy-visually-hidden role="status" aria-live="polite">
				${showing ? componentText(ANNOUNCEMENT[offer]) : ''}
			</schmancy-visually-hidden>

			${when(
				this.anchored,
				() => html`
					<!-- Bottom-centre: the bottom edge is the one strip an app's own
					     chrome leaves free at every width, where a top-centred pill lands
					     on whatever the header put in the middle. The container takes no
					     pointer events so it never eats a click meant for the page under
					     it, and the pill takes them back. The safe-area inset is what
					     keeps it off the home indicator once the app is installed and
					     the consumer's viewport meta asks for viewport-fit=cover. -->
					<div
						class="fixed bottom-0 inset-x-0 z-50 flex justify-center px-2 pointer-events-none pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
					>
						<!-- inert, not merely collapsed: the reveal directive keeps its
						     subject mounted so it has something to animate, and a
						     zero-height clipped box is still in the focus order and still in
						     the accessibility tree. A keyboard visitor on iOS would
						     otherwise tab into an invisible How-to-install at the bottom of
						     every page, and a driving agent would find it by name. inert
						     takes the whole subtree out of focus, hit-testing and the
						     accessibility tree without touching the layout the animation
						     reads. -->
						<div ${reveal(showing, { preset: 'snappy' })} class="overflow-hidden" ?inert=${!showing}>
							<schmancy-surface
								type="containerHighest"
								opacity="glass"
								elevated
								class="pointer-events-auto w-max max-w-full rounded-2xl"
							>
								<div class="flex items-center gap-3 py-1.5 pl-4 pr-1.5">
									<schmancy-icon class="text-primary-default" aria-hidden="true">
										${showing ? GLYPH[offer] : ''}
									</schmancy-icon>
									<schmancy-typography type="body" token="sm" class="whitespace-nowrap">
										${showing ? componentText(ANNOUNCEMENT[offer]) : ''}
									</schmancy-typography>
									${action}
									<schmancy-icon-button
										size="sm"
										variant="text"
										type="button"
										aria-label=${componentText('installApp.dismiss')}
										@click=${() => dismissInstall()}
									>
										close
									</schmancy-icon-button>
								</div>
							</schmancy-surface>
						</div>
					</div>
				`,
				// An unanchored host has no second gate: `showing` there IS
				// `offer !== null`, which `action` already carries.
				() => action,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-install-app': SchmancyInstallApp
	}
}
